import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutGrid, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, timeAgo, useApi } from "@/lib/api";
import { useEditor } from "./useEditorState";

const TONE = { alta: "bg-rose-50 text-rose-700 border-rose-200", media: "bg-amber-50 text-amber-800 border-amber-200", bassa: "bg-slate-50 text-slate-600 border-slate-200" };

function Issue({ i, n }) {
  return (
    <li className="rounded-md border border-slate-200 p-2 space-y-0.5 min-w-0" data-testid={`compo-issue-${n}`}>
      <p className="flex items-start gap-1.5 min-w-0">
        <span className={`shrink-0 rounded border px-1.5 text-[10px] font-medium ${TONE[i.severity]}`}>{i.severity}</span>
        <b className="text-slate-900 min-w-0 break-words">{i.page}</b>
      </p>
      <p className="text-slate-500 break-words" data-testid={`compo-issue-${n}-section`}>{i.section}</p>
      <p>{i.message}</p>
      <p className="text-slate-500">{i.hint}</p>
    </li>
  );
}

function Result({ c }) {
  if (!c) return <p className="text-slate-500" data-testid="compo-empty">Nessun controllo ancora: avvialo per cercare card ripetute, sezioni simili e hero mobile senza foto.</p>;
  return (
    <>
      <p data-testid="compo-summary">{c.issues.length ? <b className="text-amber-800">{c.issues.length} avvisi</b> : <b className="text-emerald-700">Nessuna ripetizione evidente</b>}
        {" "}su {c.sections} sezioni in {c.pages.length} pagine · {timeAgo(c.at)}</p>
      {c.stale && <p className="text-amber-700" data-testid="compo-stale">Il sito è cambiato dopo questo controllo: ricontrolla.</p>}
      {c.issues.length > 0 && <ul className="space-y-1.5" data-testid="compo-issues">{c.issues.map((i, n) => <Issue key={n} i={i} n={n} />)}</ul>}
    </>
  );
}

// Free composition check of the compiled site (no AI, no browser), stored on the project and refreshed on demand.
export default function CompositionCheck() {
  const api = useApi();
  const ed = useEditor();
  const pid = ed.project.id;
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/projects/${pid}/composition-check`).then(({ data }) => setD(data)).catch(() => setD(false)), [api, pid]);
  useEffect(() => { load(); }, [load, ed.project.generation?.finished_at]);
  const run = async () => {
    setBusy(true);
    try { await ed.save(); await api.post(`/projects/${pid}/composition-check`); await load(); toast.success("Controllo compositivo aggiornato"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!d) return null;
  return (
    <div className="rounded-lg border bg-white p-3 space-y-2 text-[11px] text-slate-700" data-testid="compo-check">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><LayoutGrid size={12} />Controllo compositivo · gratuito</p>
      <Result c={d.check} />
      {!d.available && <p className="text-amber-800" data-testid="compo-unavailable">{d.reason}</p>}
      <Button variant="outline" size="sm" className="w-full" onClick={run} disabled={busy || !d.available} data-testid="compo-recheck-btn">
        {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <RefreshCw size={13} className="mr-1.5" />}{d.check ? "Ricontrolla" : "Controlla ora"}
      </Button>
    </div>
  );
}
